/**
 * A document's sharing links (C1, 2026-10-25; extracted for the work hub, 2026-10-31): the list (who it is for, state,
 * expiry, downloads, whether it asks for a password), «Trekk tilbake» (two clicks), and «Del med lenke» — a link that
 * expires in 1–90 days, with an optional password and hint (work hub). The link and the password are shown ONCE, in
 * this answer only; the server keeps the token's SHA-256 and the password's scrypt hash. Used by Dokumenter and by
 * Styremøter («Del protokoll»). documents.share; the server decides again.
 */
import React, { useEffect, useState } from 'react';
import { API_ORIGIN, errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { Btn, CopyField, Field, Loading, LoadError, Msg, Tag, TwoStep } from './ui';
import { FormDialog } from './dialog';

const STATE_NO: Record<string, [string, 'green' | 'grey' | 'red']> = { active: ['Aktiv', 'green'], expired: ['Utløpt', 'grey'], revoked: ['Trukket tilbake', 'red'] };
const dateText = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

type ShareForm = { label: string; days: string; password: string; hint: string };
/** What is sent: an empty password is no password; a hint only with one. */
export function shareBody(f: ShareForm): Body {
  const pw = f.password.trim() ? f.password : '';
  return { label: f.label, expiresInDays: Number(f.days), ...(pw ? { password: pw, ...(f.hint.trim() ? { passwordHint: f.hint.trim() } : {}) } : {}) };
}

export function DocumentShares({ documentId, canMake = true, label = '', onChanged }: {
  documentId: string; canMake?: boolean; label?: string; onChanged?: () => void;
}) {
  const { data, error, reload } = useLoad<Body>(`/documents/${encodeURIComponent(documentId)}/shares`, [documentId]);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<ShareForm | null>(null);
  const [made, setMade] = useState<{ url: string; label: string; password: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMade(null); setForm(null); }, [documentId]);
  const make = async () => {
    if (!form || busy) return;
    setBusy(true);
    try {
      const r = await post(`/documents/${encodeURIComponent(documentId)}/shares`, shareBody(form));
      if (!r.ok) return say(errText(r.body, { label: 'Hvem er lenken til', expiresInDays: 'Gyldig i', password: 'Passord', passwordHint: 'Hint' }), 'err');
      setMade({ url: API_ORIGIN + r.body.path, label: form.label, password: r.body.password || null }); setForm(null); reload(); onChanged?.();
    } finally { setBusy(false); }
  };
  const revoke = async (s: Body) => {
    const r = await send(`/documents/${encodeURIComponent(documentId)}/shares/${s.id}/revoke`, { method: 'POST', body: '{}' });
    if (!r.ok) return say(errText(r.body), 'err');
    say(`Lenken til ${s.label} er trukket tilbake.`, 'ok'); reload(); onChanged?.();
  };
  const shares: Body[] = (data && data.shares) || [];
  return (
    <>
      <p className="note">En lenke gir hvem som helst som har den, tilgang til filen (den gjeldende versjonen) til den utløper eller trekkes tilbake. Den vises bare én gang. Et passord gjør at lenken alene ikke er nok.</p>
      {made && <>
        <CopyField label={`Lenke til ${made.label} — vises bare nå`} value={made.url} />
        {made.password && <CopyField label="Passord — vises bare nå. Send det på en annen måte enn lenken." value={made.password} />}
        <p className="note">Kopier nå. Det kan ikke vises igjen; lag en ny lenke om det blir borte.</p>
      </>}
      {canMake && <div className="row"><Btn ghost onClick={() => { setMade(null); setForm({ label, days: '14', password: '', hint: '' }); }}>Del med lenke</Btn></div>}
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : shares.length > 0 && (
        <table className="t">
          <thead><tr><th scope="col">Til</th><th scope="col">Status</th><th scope="col">Utløper</th><th scope="col">Nedlastinger</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{shares.map((s) => { const [txt, tone] = STATE_NO[s.state] || [s.state, 'grey']; return (
            <tr key={s.id}><td>{s.label}{s.hasPassword && <> <Tag tone="gold">Passord</Tag></>}{s.passwordHint && <div className="sub">Hint: {s.passwordHint}</div>}</td>
              <td><Tag tone={tone}>{txt}</Tag></td><td>{dateText(s.expiresAt)}</td><td>{s.downloadCount}</td>
              <td>{s.state === 'active' && <TwoStep label="Trekk tilbake" ariaLabel={`Trekk tilbake lenken til ${s.label}`} busyLabel="Trekker tilbake …"
                confirmLabel="Klikk igjen: lenken slutter å virke" onConfirm={() => revoke(s)} />}</td></tr>); })}</tbody>
        </table>
      )}
      <Msg text={msg.text} tone={msg.tone} />
      <FormDialog open={Boolean(form)} title="Del med lenke" onClose={() => setForm(null)}
        hint="Hvem som helst som får lenken, kan laste ned filen til den utløper. Lenken vises bare én gang.">
        {form && <>
          <div className="row">
            <Field label="Hvem er lenken til?" required className="field half" note="For eksempel «Revisor» — så dere vet hvilken lenke dere trekker tilbake.">{(fid, nd) => (
              <input id={fid} aria-describedby={nd} maxLength={80} autoComplete="off" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />)}</Field>
            <Field label="Gyldig i (dager)" required className="field half" note="1–90 dager.">{(fid, nd) => (
              <input id={fid} aria-describedby={nd} type="number" min={1} max={90} inputMode="numeric" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} />)}</Field>
          </div>
          <div className="row">
            <Field label="Passord (valgfritt)" className="field half" note="6–72 tegn. Vises bare én gang; send det på en annen måte enn lenken.">{(fid, nd) => (
              <input id={fid} aria-describedby={nd} type="password" autoComplete="new-password" maxLength={72} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />)}</Field>
            <Field label="Hint (valgfritt)" className="field half" note="Maks 40 tegn. Vises på siden som spør etter passordet — aldri selve passordet.">{(fid, nd) => (
              <input id={fid} aria-describedby={nd} maxLength={40} autoComplete="off" disabled={!form.password.trim()} value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })} />)}</Field>
          </div>
          <div className="row"><Btn small={false} disabled={busy || !form.label.trim()} onClick={make}>{busy ? 'Lager …' : 'Lag lenke'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </>
  );
}
